import { createSlice, createAsyncThunk, AnyAction } from '@reduxjs/toolkit';
import { UserDataArray, UserModel, UserQueryParams, UserItem } from '@app/interfaces/user';
import { assignRoleUserApi, changePasswordUserApi, createUserApi, deleteUserApi, getUserApi, getUsersApi, lockAcessUserApi, updateUserApi, uploadUserAvatarApi } from '@app/api/user.api';
import { persistUser, readUser } from '@app/services/localStorage.service';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface UserState {
  data: UserModel[];
  totalRows: number;
  pagination: {
    current: number,
    pageSize: number
  },
  filters: any,
  sorter: any,
  searchTerm: string;
  selectedRecord: UserItem[];
  loading: boolean;
  error: string | null;
  user: UserModel | null;
  currentUserAvatarUrl: string | null;
}

const initialState: UserState = {
  data: [],
  totalRows: 0,
  pagination: { current: 1, pageSize: 20 },
  filters: {},
  sorter: {},
  searchTerm: "",
  selectedRecord: [],
  loading: false,
  error: null,
  user: null,
  currentUserAvatarUrl: readUser()?.avatar_url ?? null,
};

interface ResponseData {
  data: UserItem;
  message: string;
  error: string;
}

interface LockAccessParams {
  userId: number;
  formData: { status: boolean };
}

interface ChangePasswordParams {
  userId: number;
  formData: { 
    current_password: string;
    password: string;
    password_confirmation: string
   }; 
}

export const getUsers = createAsyncThunk<UserDataArray, UserQueryParams>(
  'user/getUsers',
  async (params: UserQueryParams, { rejectWithValue }) => {
    try {
      const data = (await getUsersApi(params)) as UserDataArray;
      return data || [];
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const getUser = createAsyncThunk<ResponseData, number>(
  'user/getUser',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await getUserApi(userId);
      return data;

    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const createUser = createAsyncThunk<ResponseData, UserModel>(
  'user/createUser', async (data) => {
  const response = await createUserApi(data);
  return response;
});

export const updateUser = createAsyncThunk<ResponseData, { userId: number; formData: UserModel }>(
  'user/updateUser',
  async ({ userId, formData }) => {
    const response = await updateUserApi(userId, formData);
    return response;
  },
);

export const deleteUser = createAsyncThunk<{ userId: number; response: any }, number>(
  'user/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await deleteUserApi(userId);
      return { userId, response };
    } catch (error) {
      // Handle errors with rejectWithValue
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const lockAccessUser = createAsyncThunk<ResponseData, LockAccessParams>(
  'user/lockAccessUser',
  async ({userId, formData}, { rejectWithValue }) => {
    try {
      const data = await lockAcessUserApi(userId, formData);
      return data;

    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const changePasswordUser = createAsyncThunk<ResponseData, ChangePasswordParams>(
  'user/changePasswordUser',
  async ({userId, formData}, { rejectWithValue }) => {
    try {
      const data = await changePasswordUserApi(userId, formData);
      return data;

    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'An error occurred'
      );
    }
  }
);

export const assignRoleUser = createAsyncThunk<ResponseData, { userId: number; formData: any }>(
  'user/assignRoleUser',
  async ({ userId, formData }) => {
    const response = await assignRoleUserApi(userId, formData);
    return response;
  },
);

export const uploadUserAvatar = createAsyncThunk<ResponseData, { userId: number; file: File }>(
  'user/uploadUserAvatar',
  async ({ userId, file }, { rejectWithValue }) => {
    try {
      const response = await uploadUserAvatarApi(userId, file);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Upload failed');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateTableParams(state, action) {
      const { pagination, filters, sorter, searchTerm } = action.payload;
      state.pagination = pagination;
      state.filters = filters;
      state.sorter = sorter;
      state.searchTerm = searchTerm;
    },
    setCurrentUserAvatarUrl(state, action) {
      state.currentUserAvatarUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(getUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getUsers.fulfilled, (state, action) => {
      const { rows, total_rows } = action.payload;

      const data = rows;
      state.data = data.map((item: UserItem) => item.attributes) || [];

      state.totalRows = parseInt(total_rows, 10);
      state.loading = false;
    })
    .addCase(createUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      state.data.unshift(data.attributes);
      message.success(newMessage);
    })
    .addCase(getUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;

      if (data) {
        state.user = data.attributes;
        //message.success(newMessage);
      }
    })
    .addCase(updateUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      const userId = Number(data.id);

      const updatedIndex = state.data.findIndex((item) => Number(item.id) === userId);
      if (updatedIndex !== -1) {
        state.data[updatedIndex] = data.attributes;
        state.user = data.attributes;
        message.success(newMessage);
      }
    })
    .addCase(deleteUser.fulfilled, (state, action) => {
      const { userId, response } = action.payload;

      if (response.error) { 
        showError(response.error); return;
      }
      else {
        state.data = state.data.filter((order) => order.id !== userId);
        message.success(response.message);
      }
    })
    .addCase(lockAccessUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;

      if (data) {
        // Có thể gọi từ danh sách hoặc trang chi tiết → đồng bộ cả hai nơi.
        const updated = data.attributes;
        const updatedId = Number(updated.id);
        state.data = state.data.map((item) => (Number(item.id) === updatedId ? updated : item));
        if (Number(state.user?.id) === updatedId) state.user = updated;
        message.success(newMessage);
      }
    })
    .addCase(changePasswordUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      if (data) {
        state.user = data.attributes;
        message.success(newMessage);
      }
    })
    .addCase(assignRoleUser.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      if (data) {
        state.user = data.attributes;
        message.success(newMessage);
      }
    })
    .addCase(uploadUserAvatar.fulfilled, (state, action) => {
      const { data, message: newMessage } = action.payload;
      if (data) {
        const avatarUrl = data.attributes?.avatar_url ?? null;
        state.currentUserAvatarUrl = avatarUrl;
        // Sync to localStorage so readUser() stays consistent
        const stored = readUser();
        if (stored) {
          persistUser({ ...stored, avatar_url: avatarUrl ?? undefined });
        }
        message.success(newMessage || 'Cập nhật avatar thành công');
      }
    })
    .addMatcher(isRejectedAction, (state, action) => {
      state.loading = false;
      // rejectWithValue trong slice này truyền chuỗi (vd lý do không thể xoá user).
      const payloadMessage = typeof action.payload === 'string' ? action.payload : undefined;
      showError(payloadMessage || action.payload?.message || action.payload?.error || action?.error?.message);
    });
  },
});

export const { updateTableParams, setCurrentUserAvatarUrl } = userSlice.actions;

export default userSlice.reducer;
